import React from 'react';
import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from 'components/mui';
import { Skeleton, TablePagination, Typography } from '@mui/material';
import { CANtrackComment } from '../../types/comment';
import CaseCommentsTableRow from './CaseCommentsTableRow';

interface CaseCommentsTableProps {
	comments: CANtrackComment[];
	loadingComments: boolean;
	showDecimalFormat: boolean;
	setComments: React.Dispatch<React.SetStateAction<CANtrackComment[]>>;
	onCopyCommentClick: (commentToCopy: CANtrackComment) => void;
	hideCaseInfo?: boolean;
	page?: number;
	totalResults?: number;
	rowsPerPage?: number;
	onPageChange?: (_event: unknown, newPage: number) => void;
	sortNewestFirst?: boolean;
}

const LoadingRows: React.FC<{ count: number }> = ({ count }) => (
	<>
		{[...Array(count)].map((_, index) => (
			<TableRow key={index}>
				<TableCell colSpan={8} align="center">
					<Typography variant="h5" align="center">
						<Skeleton variant="rounded" animation="wave" />
					</Typography>
				</TableCell>
			</TableRow>
		))}
	</>
);

const NoEntries: React.FC = () => (
	<TableRow>
		<TableCell colSpan={8}>
			<Typography
				variant="h5"
				align="center"
				fontWeight={300}
				color={'warning.main'}
			>
				No entries found
			</Typography>
		</TableCell>
	</TableRow>
);

const CaseCommentsTable: React.FC<CaseCommentsTableProps> = (props) => {
	const {
		comments,
		loadingComments,
		showDecimalFormat,
		setComments,
		onCopyCommentClick,
		hideCaseInfo,
		page,
		totalResults,
		rowsPerPage,
		onPageChange,
		sortNewestFirst,
	} = props;
	const showPagination = onPageChange !== undefined && totalResults !== undefined;
	const commentsSorted = [...comments].sort((a, b) =>
		sortNewestFirst
			? b.date.getTime() - a.date.getTime()
			: a.date.getTime() - b.date.getTime(),
	);

	return (
		<div>
			<TableContainer component={Paper} sx={{ overflowX: { xs: 'hidden', md: 'auto' } }}>
				<Table sx={{ minWidth: { xs: 'unset', md: 650 } }} size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }} />
							{!hideCaseInfo && <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Client</TableCell>}
							{!hideCaseInfo && <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }} align="left">Case</TableCell>}
							<TableCell align="left">Summary</TableCell>
							<TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }} align="left">Entry&nbsp;Time</TableCell>
							<TableCell align="left">Time&nbsp;Spent</TableCell>
							<TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }} align="center">Billable</TableCell>
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{loadingComments ? (
							<LoadingRows
								count={
									comments.length > 0 ? comments.length : 5
								}
							/>
						) : (
							<>
								{commentsSorted.length === 0 && <NoEntries />}
								{commentsSorted.map((entry) => (
									<CaseCommentsTableRow
										key={entry.id}
										comment={entry}
										setComments={setComments}
										decimalFormat={showDecimalFormat}
										onCopyCommentClick={onCopyCommentClick}
										hideCaseInfo={hideCaseInfo}
									/>
								))}
							</>
						)}
					</TableBody>
				</Table>
			</TableContainer>
			{showPagination && (
				<TablePagination
					component="div"
					count={totalResults}
					page={page ?? 0}
					rowsPerPage={rowsPerPage ?? 50}
					onPageChange={(_e, newPage) => onPageChange(_e, newPage)}
					rowsPerPageOptions={[]}
				/>
			)}
		</div>
	);
};

export default CaseCommentsTable;
